import React, { useEffect, useState } from 'react';

import {
    FormControl,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    TextField
} from '@mui/material';
import ClearAllRoundedIcon from '@mui/icons-material/ClearAllRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import DayRangePicker from '../../components/dayRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import { FASE_OPTIONS, ORIGEM_OPTIONS, VENDEDOR_OPTIONS, buildGroupedStatusOptions } from './crmShared';
import { CRM_LEAD_DATE_FILTER_RANGES, CRM_LEAD_INTEGER_FILTER_FIELDS, CRM_LEAD_TEXT_FILTER_FIELDS, CRM_LEAD_TICKET_ORDER_FIELD, toIdArray } from './crmLeadsData';

const selectConfig = {
    vendedor_id: { allLabel: 'Todos', options: VENDEDOR_OPTIONS },
    fase_id: { allLabel: 'Todas', options: FASE_OPTIONS },
    origem_id: { allLabel: 'Todas', options: ORIGEM_OPTIONS },
    ordem_id: {
        options: [
            { id: 0, label: 'Nenhum' },
            { id: 1, label: 'Descendente' },
            { id: 2, label: 'Ascendente' }
        ]
    }
};

const allMultiSelectOption = 'all';

const multiSelectConfig = {
    status_id: [
        { value: allMultiSelectOption, label: 'Todos' },
        ...buildGroupedStatusOptions()
    ]
};

const columnFilterFields = {
    fase: 'fase_id',
    origem: 'origem_id',
    status: 'status_id',
    ticket: CRM_LEAD_TICKET_ORDER_FIELD,
    vendedor: 'vendedor_id'
};

const marketingFields = new Set(['source', 'campaign', 'content', 'term']);

const selectMenuProps = {
    PaperProps: { className: 'crm-dashboard-select-menu-paper' },
    MenuListProps: { className: 'crm-dashboard-select-menu-list' }
};

const renderSelectFilter = (field, filters, onFilterChange, ariaLabel) => {
    const config = selectConfig[field];

    return (
        <FormControl size='small' className='crm-leads-column-filter-control'>
            <Select
                aria-label={`Filtrar por ${ariaLabel}`}
                value={filters[field]}
                onChange={(e) => onFilterChange(field, e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
            >
                {config.allLabel && <MenuItem className='crm-dashboard-select-menu-item' value=''>{config.allLabel}</MenuItem>}
                {config.options.map((option) => (
                    <MenuItem className='crm-dashboard-select-menu-item' key={option.id} value={option.id}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const areSameIdLists = (currentIds, nextIds) => (
    currentIds.length === nextIds.length &&
    currentIds.every((id, index) => id === nextIds[index])
);

const MultiSelectColumnFilter = ({ ariaLabel, field, filters, onFilterChange }) => {
    const appliedValues = toIdArray(filters[field]);
    const [draftValues, setDraftValues] = useState(appliedValues);

    useEffect(() => {
        setDraftValues(toIdArray(filters[field]));
    }, [field, filters]);

    const handleClose = () => {
        if (areSameIdLists(appliedValues, draftValues)) return;
        onFilterChange(field, draftValues);
    };

    return (
        <MultiSelectFilter
            className='crm-leads-column-filter-control'
            size='small'
            ariaLabel={`Filtrar por ${ariaLabel}`}
            value={draftValues}
            options={multiSelectConfig[field]}
            allOptionValue={allMultiSelectOption}
            onChange={(values) => setDraftValues(values.includes(allMultiSelectOption) ? [] : values)}
            onClose={handleClose}
        />
    );
};

const DateRangeFilter = ({ column, dateFilters, onDateRangeApply }) => {
    const range = CRM_LEAD_DATE_FILTER_RANGES[column.field];
    const appliedStartDate = dateFilters[range.start];
    const appliedEndDate = dateFilters[range.end];

    return (
        <DayRangePicker
            ariaLabel={`Filtrar ${column.headerName} por período`}
            startDate={appliedStartDate}
            endDate={appliedEndDate}
            maxDate={new Date()}
            onApply={(startDate, endDate) => onDateRangeApply(range, startDate, endDate)}
            onClear={() => onDateRangeApply(range, null, null)}
        />
    );
};

const renderColumnFilter = ({
    canViewMarketingFilters,
    column,
    dateFilters,
    filters,
    onDateRangeApply,
    onFilterChange
}) => {
    if (marketingFields.has(column.field) && !canViewMarketingFilters) return null;

    const filterField = columnFilterFields[column.field] ?? column.field;

    if (CRM_LEAD_DATE_FILTER_RANGES[column.field]) {
        return (
            <DateRangeFilter
                column={column}
                dateFilters={dateFilters}
                onDateRangeApply={onDateRangeApply}
            />
        );
    }

    if (multiSelectConfig[filterField]) {
        return (
            <MultiSelectColumnFilter
                ariaLabel={column.headerName}
                field={filterField}
                filters={filters}
                onFilterChange={onFilterChange}
            />
        );
    }

    if (selectConfig[filterField]) {
        return renderSelectFilter(filterField, filters, onFilterChange, column.headerName);
    }

    if (CRM_LEAD_TEXT_FILTER_FIELDS.includes(filterField) || CRM_LEAD_INTEGER_FILTER_FIELDS.includes(filterField)) {
        return (
            <TextField
                size='small'
                type={CRM_LEAD_INTEGER_FILTER_FIELDS.includes(filterField) ? 'number' : 'text'}
                value={filters[filterField] ?? ''}
                onChange={(event) => onFilterChange(filterField, event.target.value)}
                placeholder='Filtrar'
                inputProps={{ 'aria-label': `Filtrar por ${column.headerName}` }}
                fullWidth
                className='crm-leads-column-text-filter'
            />
        );
    }

    return null;
};

export const CrmLeadsFilters = ({
    filters,
    onFilterChange,
    dateFilters,
    onDateRangeApply,
    onClearFilters,
    canViewMarketingFilters,
    columns
}) => {
    return (
        <TableRow className='crm-leads-filter-row'>
            <TableCell className='crm-leads-filter-cell crm-leads-actions-cell'>
                <button
                    type='button'
                    className='crm-leads-clear-button'
                    aria-label='Limpar todos os filtros'
                    title='Limpar filtros'
                    onClick={onClearFilters}
                >
                    <ClearAllRoundedIcon fontSize='inherit' />
                </button>
            </TableCell>

            {columns.map((column) => (
                <TableCell
                    key={column.field}
                    className='crm-leads-filter-cell'
                    data-field={column.field}
                >
                    {renderColumnFilter({
                        canViewMarketingFilters,
                        column,
                        dateFilters,
                        filters,
                        onDateRangeApply,
                        onFilterChange
                    })}
                </TableCell>
            ))}
        </TableRow>
    );
};
